<template>
  <nav class="visitorNavbar">
    <router-link v-if="navbarBrand === 'logo'" class="logoContainer" to="/">
      <img :src="logoSrc" loading="eager" :alt="logoAlt" @error="logoError = true">
    </router-link>
    <router-link v-else-if="navbarBrand === 'name'" class="logoContainer logo-text" to="/">
      {{ artistConfig.artist?.name || artistConfig.site?.title || '' }}
    </router-link>

    <!-- Hidden: full nav for width measurement. Always in DOM. -->
    <div ref="navMeasure" class="nav-measure">
      <router-link v-for="item in navItems" :key="'m-' + item.to" class="nav-item" :to="item.to" tabindex="-1">
        {{ item.label }}
      </router-link>
      <span ref="moreMeasure" class="nav-item">MORE</span>
    </div>

    <!-- Visible desktop nav -->
    <div v-if="!useHamburger" class="visitor-btns"
      :class="{ 'is-loading': isLoading, 'no-logo': navbarBrand === 'none' }">
      <router-link v-for="item in visibleItems" :key="item.to" class="nav-item" :to="item.to">
        {{ item.label }}
      </router-link>

      <div v-if="moreItems.length" class="more-container">
        <button class="nav-item more-toggle" title="More navigation links" aria-label="More navigation links"
          @click="moreOpen = !moreOpen">
          MORE
          <ChevronDownIcon class="more-chevron" :class="{ open: moreOpen }" />
        </button>
        <div v-if="moreOpen" class="more-menu">
          <router-link v-for="item in moreItems" :key="item.to" class="more-link" :to="item.to"
            @click="moreOpen = false">
            {{ item.label }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Hamburger for small screens -->
    <BaseResponsiveNav v-if="useHamburger" :items="navItems" />
  </nav>
</template>

<script>
import { store, actions } from '@/store'
import { artistConfig as defaultConfig } from '@/config/artist.config'
import { wrapperToDirectUrl } from '@/utils/imageUrl'
import { pageWidth } from '@/utils/pageWidth'
import BaseResponsiveNav from './BaseResponsiveNav.vue'
import ChevronDownIcon from '@/assets/icons/chevron-down.vue'

export default {
  name: 'VisitorNavbar',
  components: { BaseResponsiveNav, ChevronDownIcon },
  props: {
    config: { type: Object, default: null },
    preview: { type: Boolean, default: false }
  },
  data() {
    return {
      // Previews draw a desktop page, so they never start on the hamburger
      useHamburger: !this.preview && typeof window !== 'undefined' && window.innerWidth < 600,
      visibleCount: Infinity,
      moreOpen: false,
      isLoading: true,
      logoError: false
    }
  },
  computed: {
    artistConfig() {
      return this.config || store.artistConfig || defaultConfig
    },
    logoUrl() {
      return this.artistConfig.theme?.logoUrl || ''
    },
    // Edge-cached direct GCS URL; falls back to the stored wrapper URL on error.
    logoSrc() {
      if (this.logoError) return this.logoUrl
      return wrapperToDirectUrl(this.logoUrl) || this.logoUrl
    },
    logoAlt() {
      return this.artistConfig.artist?.name || this.artistConfig.site?.title || 'Logo'
    },
    navbarBrand() {
      const theme = this.artistConfig.theme
      if (theme?.navbarBrand) return theme.navbarBrand
      // migrate from old showLogo boolean
      if (theme?.showLogo === false) return 'name'
      return theme?.logoUrl ? 'logo' : 'name'
    },
    visibleCustomPages() {
      return store.customPages
        .filter(page => !page.isHidden && page.showInNav !== false && page.id !== '_home')
        .sort((a, b) => (a.position || 0) - (b.position || 0))
    },
    navItems() {
      return [
        { label: 'HOME', to: '/' },
        ...this.visibleCustomPages.map(page => ({
          label: (page.title || '').toUpperCase(),
          to: `/${page.slug}`
        }))
      ]
    },
    visibleItems() {
      return this.navItems.slice(0, this.visibleCount)
    },
    moreItems() {
      if (this.visibleCount >= this.navItems.length) return []
      return this.navItems.slice(this.visibleCount)
    }
  },
  watch: {
    navItems() {
      this.$nextTick(() => this.checkOverflow())
    },
    '$route'() {
      this.moreOpen = false
    }
  },
  async mounted() {
    if (store.customPages.length > 0) this.isLoading = false
    await actions.loadCustomPages({ navOnly: true })
    this.isLoading = false
    await this.$nextTick()
    this.checkOverflow()
    if (this.preview) return
    if (!this.$el || !this.$refs.navMeasure) return
    this._observer = new ResizeObserver(() => requestAnimationFrame(() => this.checkOverflow()))
    this._observer.observe(this.$el)
    this._observer.observe(this.$refs.navMeasure)

    this._outsideClick = (e) => {
      if (this.moreOpen && !this.$el.contains(e.target)) {
        this.moreOpen = false
      }
    }
    this._escapeKey = (e) => {
      if (e.key === 'Escape' && this.moreOpen) {
        this.moreOpen = false
      }
    }
    document.addEventListener('click', this._outsideClick)
    document.addEventListener('keydown', this._escapeKey)
  },
  beforeUnmount() {
    this._observer?.disconnect()
    document.removeEventListener('click', this._outsideClick)
    document.removeEventListener('keydown', this._escapeKey)
  },
  methods: {
    checkOverflow() {
      if (this.isLoading) return
      const measure = this.$refs.navMeasure
      if (!measure) return

      const logo = this.$el.querySelector('.logoContainer')
      const logoWidth = logo ? logo.offsetWidth : 0
      const gap = 10
      const padding = 84 // --nav-edge 42px on each side
      const clientWidth = pageWidth(this.$el)
      if (!clientWidth) return // not laid out yet
      const available = clientWidth - logoWidth - padding

      // Measure each nav item from the hidden div (last child is the MORE element)
      const children = Array.from(measure.children)
      const moreEl = this.$refs.moreMeasure
      const moreWidth = moreEl ? moreEl.offsetWidth : 80
      const itemEls = children.filter(el => el !== moreEl)
      const itemWidths = itemEls.map(el => el.offsetWidth)

      // Check if all items fit without "More"
      let total = 0
      for (let i = 0; i < itemWidths.length; i++) {
        total += itemWidths[i] + (i > 0 ? gap : 0)
      }

      if (total <= available) {
        this.visibleCount = itemWidths.length
        this.useHamburger = false
        return
      }

      // Calculate how many fit alongside the "More" button
      let used = moreWidth
      let count = 0
      for (let i = 0; i < itemWidths.length; i++) {
        const addition = itemWidths[i] + gap
        if (used + addition <= available) {
          used += addition
          count++
        } else {
          break
        }
      }

      if (count >= 3) {
        this.visibleCount = count
        this.useHamburger = false
      } else {
        this.visibleCount = 0
        this.useHamburger = true
      }
    }
  }
}
</script>

<style scoped>
.visitorNavbar {
  --nav-edge: 42px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  position: relative;
  z-index: 101;
  background-color: var(--color-header);
  padding: 10px 0;
  min-height: 50px;
}

.logoContainer {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  margin-left: var(--nav-edge);
}

.logoContainer img {
  display: block;
  /* Fixed height reserves the navbar's vertical space before the image loads (CLS) */
  height: 60px;
  width: auto;
  max-width: 250px;
  object-fit: contain;
}

.logo-text {
  font-family: var(--font-decorative);
  font-size: var(--font-lg);
  color: var(--color-header-text, #fff);
  text-decoration: none;
  white-space: nowrap;
}

.nav-measure {
  position: absolute;
  left: -9999px;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  flex: 1 0 auto;
  gap: 10px;
}

.visitor-btns {
  display: flex;
  flex: 1 0 auto;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-right: var(--nav-edge);
  transition: opacity 0.15s ease;

  &.no-logo {
    justify-content: center;
  }
}

.visitor-btns.is-loading {
  opacity: 0;
  pointer-events: none;
}

/* Zero out right padding on last item so its edge sits exactly at --nav-edge */
.visitor-btns>.nav-item:last-child,
.visitor-btns>.more-container:last-child .more-toggle {
  padding-right: 0;
}

.more-container {
  position: relative;
}

.more-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
}

.more-chevron {
  transition: transform 0.2s ease;

  &.open {
    transform: rotate(180deg);
  }
}

.more-menu {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  background-color: var(--color-header);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0 0 var(--radius, 4px) var(--radius, 4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
}

.more-link {
  text-decoration: none;
  font-family: var(--font-decorative, 'Prata', serif);
  color: var(--color-header-text);
  font-size: var(--font-sm);
  padding: 12px 20px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: background-color 0.15s;
}

.more-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.more-link:last-child {
  border-radius: 0 0 var(--radius, 4px) var(--radius, 4px);
}
</style>
